import { useEffect, useState } from "react";

const MIN_DESKTOP_PX = 900;

const isReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isTouch = () =>
  typeof window !== "undefined" && "ontouchstart" in window;

export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
};

// Adds 'is-visible' class when the element enters the viewport. One-shot.
export const useReveal = (ref, threshold = 0.12) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (isReducedMotion()) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
};

// Animates a numeric value from 0 to the number found in `finalText`,
// preserving any prefix/suffix (e.g. "R", "%", " hrs"). One-shot.
export const useCountUp = (ref, finalText, opts = {}) => {
  const { threshold = 0.4, duration = 1100 } = opts;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.textContent = finalText;
    if (isReducedMotion()) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (el.dataset.done) return;
          el.dataset.done = "1";
          const m = finalText.match(/(\d+(?:\.\d+)?)/);
          if (!m) return;
          const target = parseFloat(m[1]);
          const prefix = finalText.slice(0, m.index);
          const suffix = finalText.slice(m.index + m[0].length);
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            const cur = target * eased;
            const formatted = target % 1 === 0 ? Math.round(cur) : cur.toFixed(1);
            el.textContent = prefix + formatted + suffix;
            if (p < 1) requestAnimationFrame(tick);
            else el.textContent = finalText;
          };
          requestAnimationFrame(tick);
          io.unobserve(el);
        });
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, finalText, threshold, duration]);
};

export const useCursorGlow = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.innerWidth < MIN_DESKTOP_PX) return;
    if (isReducedMotion()) return;
    if (isTouch()) return;
    const onMove = (e) => {
      el.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
    };
    document.addEventListener("mousemove", onMove);
    return () => document.removeEventListener("mousemove", onMove);
  }, [ref]);
};

// Sets the initial perspective tilt on mount, then tracks mousemove on
// desktop to update the rotateX/rotateY by ±4° based on cursor offset.
// Reduced-motion / mobile / touch leave the static initial tilt in place.
export const useParallaxTilt = (ref) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "perspective(1800px) rotateX(8deg)";
    if (window.innerWidth < MIN_DESKTOP_PX) return;
    if (isReducedMotion()) return;
    if (isTouch()) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      el.style.transform = `perspective(1800px) rotateX(${8 - dy * 4}deg) rotateY(${dx * 4}deg)`;
    };
    document.addEventListener("mousemove", onMove);
    return () => document.removeEventListener("mousemove", onMove);
  }, [ref]);
};
